import { useEffect, useState } from "react";
import {
  isComponentInspectorEnabled,
  setComponentInspectorEnabled,
  getInspectorActivationKey,
  setInspectorActivationKey,
} from "../Inspector/inspectorState";
import {
  DevToggleRow,
  DevValueRow,
  type DevMenuTone,
} from "./DevMenuPrimitives";

interface RowProps {
  indented?: boolean | undefined;
  tone?: DevMenuTone | undefined;
}

/**
 * Toggles the Component Inspector on/off and reloads the page so the
 * keyboard listeners attach (or detach) cleanly. Reloading here matches
 * Hubble's behaviour and avoids surprising stale-listener bugs.
 */
export function InspectorToggleRow({ indented, tone }: RowProps) {
  const [enabled, setEnabled] = useState(isComponentInspectorEnabled);

  function handleToggle() {
    const next = !enabled;
    setComponentInspectorEnabled(next);
    setEnabled(next);
    window.location.reload();
  }

  return (
    <DevToggleRow
      label="Inspector"
      enabled={enabled}
      onToggle={handleToggle}
      indented={indented}
      tone={tone}
    />
  );
}

/**
 * Shows the current activation key for the inspector and lets the user
 * rebind it: click → "Press a key…" → next keydown stores that key.
 */
export function InspectorKeyRow({ indented, tone }: RowProps) {
  const [key] = useState(getInspectorActivationKey);
  const [rebinding, setRebinding] = useState(false);

  useEffect(() => {
    if (!rebinding) return;
    function handleRebind(e: KeyboardEvent) {
      e.preventDefault();
      setInspectorActivationKey(e.key);
      window.location.reload();
    }
    window.addEventListener("keydown", handleRebind);
    return () => window.removeEventListener("keydown", handleRebind);
  }, [rebinding]);

  return (
    <DevValueRow
      label="Inspector Key"
      value={rebinding ? "Press a key…" : key}
      onClick={() => setRebinding(true)}
      indented={indented}
      tone={tone}
    />
  );
}
